import { CustomFieldDefinition } from "../../../gen/clients/llts";
import { IdTokenClaims } from "../../../utils/AuthUtils";
import { sanitizeFieldName } from "../formikUtils";

const VARIABLE_REGEX = /^\{\{(.*)}}$/;
const IDENTITY_VARIABLE_PREFIX = "identity.";

export class CustomFieldUtils {
  /**
   * Determines the visibility of a custom field based on its definition and the current form values.
   *
   * @param {CustomFieldDefinition} customFieldDefinition - The definition of the custom field, including visibility rules.
   * @param {Record<string, any>} formValues - The current values of the form fields, used for conditional visibility checks.
   * @return {boolean} True if the custom field is visible, otherwise false.
   */
  public static isCustomFieldVisible(customFieldDefinition: CustomFieldDefinition, formValues: Record<string, any>) {
    if (customFieldDefinition.visible) {
      return true;
    }
    if (customFieldDefinition.visibleIf && customFieldDefinition.visibleIf.length > 0) {
      const visibleIf = customFieldDefinition.visibleIf[0];
      return formValues[sanitizeFieldName(visibleIf.fieldName)] === visibleIf.value;
    }
    return false;
  }

  /**
   * Resolves a custom field default value. A value wrapped in {{ and }} is treated as a variable:
   * {{identity.<claim>}} reads <claim> from the identity token (e.g. {{identity.custom:billingNumber}}).
   * Variables resolve to an empty string when the user is not signed in, the claim is missing,
   * or the variable is not supported.
   *
   * @param {string | undefined} defaultValue - The default value from the custom field definition.
   * @param {IdTokenClaims | undefined} idTokenClaims - Claims of the current identity token; undefined when signed out.
   * @return {string} The resolved default value.
   */
  public static resolveDefaultValue(
    defaultValue: string | undefined,
    idTokenClaims: IdTokenClaims | undefined
  ): string {
    const match = defaultValue?.match(VARIABLE_REGEX);
    if (!match) {
      return defaultValue || "";
    }
    const variableName = match[1].trim();
    if (variableName.startsWith(IDENTITY_VARIABLE_PREFIX)) {
      const claimValue = idTokenClaims?.[variableName.substring(IDENTITY_VARIABLE_PREFIX.length)];
      if (typeof claimValue === "string" || typeof claimValue === "number" || typeof claimValue === "boolean") {
        return `${claimValue}`;
      }
    }
    return "";
  }

  /**
   * Builds formik initial values for the given custom fields from their resolved default values.
   *
   * @param {CustomFieldDefinition[] | undefined} customFieldDefinitions - The custom field definitions.
   * @param {IdTokenClaims | undefined} idTokenClaims - Claims of the current identity token; undefined when signed out.
   * @return {Record<string, string>} Initial values keyed by sanitized field name.
   */
  public static getInitialValues(
    customFieldDefinitions: CustomFieldDefinition[] | undefined,
    idTokenClaims: IdTokenClaims | undefined
  ): Record<string, string> {
    const initialValues: Record<string, string> = {};
    customFieldDefinitions?.forEach(customFieldDefinition => {
      initialValues[sanitizeFieldName(customFieldDefinition.name)] = CustomFieldUtils.resolveDefaultValue(
        customFieldDefinition.defaultValue,
        idTokenClaims
      );
    });
    return initialValues;
  }
}
